import { BASE_URL } from "../config";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";

export { defaultProfilePic };
export const DEFAULT_AVATAR_SRC = defaultProfilePic;

export function apiBaseUrl() {
  return String(
    process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      BASE_URL ||
      "http://localhost:5000",
  ).replace(/\/+$/, "");
}

export function extractProfileImageVersionFromUrl(profileImageUrl) {
  if (!profileImageUrl || typeof profileImageUrl !== "string") return null;
  const m = String(profileImageUrl).match(/\/(\d+)\.(?:png|jpe?g|webp)(?:\?|$)/i);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveBackendUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/api/") || trimmed.startsWith("/auth/")) {
    return `${apiBaseUrl()}${trimmed}`;
  }
  return trimmed;
}

function collectLegacyProfileUrl(emp) {
  if (!emp || typeof emp !== "object") return "";
  const keys = [
    "profile_picture",
    "profilePicture",
    "profile_photo",
    "profile_image",
    "profile_image_url",
    "avatar_url",
    "avatar",
    "photo",
    "photo_url",
    "picture",
    "picture_url",
    "image",
  ];
  for (const k of keys) {
    const v = emp[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return "";
}

/**
 * Image src for account staff — prefers /api/avatars/account/:id so private S3 URLs work.
 */
/** Account id + minimal employee row for {@link StaffAvatarCircle} from a shift payload. */
export function shiftStaffEmployeeForAvatar(shift) {
  if (!shift || typeof shift !== "object") {
    return { accountId: null, employee: null };
  }
  const accountId = Number(
    shift.assigned_staff_id ??
      shift.assigned_account_id ??
      shift.staff_account_id ??
      shift.personnel_account_id ??
      shift.staffAccountId ??
      shift.account_id ??
      shift.accountId ??
      shift.shiftAccountId ??
      shift.shift_account_id ??
      shift.account?.account_id ??
      shift.account?.accountId ??
      shift.account?.id,
  );
  const id = Number.isFinite(accountId) && accountId > 0 ? accountId : null;
  const acc = shift.account;
  if (acc && typeof acc === "object") {
    const aid =
      Number(acc.account_id ?? acc.accountId ?? acc.id) || id || null;
    const resolvedId =
      Number.isFinite(aid) && aid > 0 ? aid : id;
    return {
      accountId: resolvedId,
      employee: {
        account_id: resolvedId,
        accountId: resolvedId,
        first_name: acc.firstName ?? acc.first_name,
        last_name: acc.lastName ?? acc.last_name,
        name: acc.name,
        profile_image: acc.profile_image ?? shift.profile_image,
        profile_picture: acc.profile_picture ?? shift.profile_picture,
        profile_photo: acc.profile_photo ?? shift.profile_photo,
        avatar_url: acc.avatar_url ?? shift.avatar_url,
      },
    };
  }
  if (id) {
    return {
      accountId: id,
      employee: {
        account_id: id,
        accountId: id,
        first_name: shift.first_name,
        last_name: shift.last_name,
        name:
          shift.assigned_staff_name ??
          shift.staffDisplay ??
          [shift.first_name, shift.last_name].filter(Boolean).join(" ").trim(),
        profile_image: shift.profile_image,
        profile_picture: shift.profile_picture,
        profile_photo: shift.profile_photo,
        avatar_url: shift.avatar_url,
      },
    };
  }
  return { accountId: null, employee: null };
}

export function accountHasAvatarMetadata(emp) {
  if (!emp || typeof emp !== "object") return false;
  const imageType = emp.image_type;
  const imageTime = emp.image_time;
  if (imageType != null && String(imageType).trim() !== "") return true;
  if (imageTime != null && Number(imageTime) > 0) return true;
  return Boolean(collectLegacyProfileUrl(emp));
}

export function employeeRowForAvatar(row) {
  if (!row || typeof row !== "object") return null;
  const accountId = row.account_id ?? row.id;
  return {
    account_id: accountId,
    accountId,
    name: row.name,
    email: row.email,
    first_name: row.first_name,
    last_name: row.last_name,
    image_type: row.image_type,
    image_time: row.image_time,
    profile_image_url: row.profile_image_url,
    phone_mobile: row.phone ?? row.phone_mobile,
  };
}

export async function fetchAccountAvatarBatch(ids) {
  const uniqueIds = [
    ...new Set(
      (ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];
  if (uniqueIds.length === 0) return {};

  const base = apiBaseUrl();
  const res = await fetch(`${base}/api/avatars/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ kind: "account", ids: uniqueIds }),
  });
  if (!res.ok) return {};
  const data = await res.json();
  return data.urls || {};
}

export function accountAvatarImgSrc(accountId, emp, { allowProxyWithoutMetadata = true } = {}) {
  const base = apiBaseUrl();
  const id = Number(
    accountId ?? emp?.account_id ?? emp?.accountId ?? emp?.id,
  );
  const legacy = collectLegacyProfileUrl(emp);

  if (legacy) {
    if (legacy.startsWith("/api/")) return `${base}${legacy}`;
    return resolveBackendUrl(legacy) || legacy;
  }

  if (!Number.isFinite(id) || id <= 0 || !base) {
    return "";
  }

  if (!allowProxyWithoutMetadata && !accountHasAvatarMetadata(emp)) {
    return "";
  }

  const imageTime = emp?.image_time;
  const v =
    imageTime != null && Number(imageTime) > 0
      ? Number(imageTime)
      : extractProfileImageVersionFromUrl(legacy);
  return v
    ? `${base}/api/avatars/account/${id}?v=${v}`
    : `${base}/api/avatars/account/${id}`;
}
